import api from './api';

const locationService = {
  // Get all locations for the organization
  async getAllLocations() {
    return api.get('/locations');
  },

  // Get locations accessible by current user
  async getMyLocations() {
    return api.get('/locations/my-locations');
  },

  // Get location by ID
  async getLocationById(id) {
    return api.get(`/locations/${id}`);
  },

  // Create new location
  async createLocation(data) {
    return api.post('/locations', data);
  },

  // Update location
  async updateLocation(id, data) {
    return api.put(`/locations/${id}`, data);
  },

  // Delete (deactivate) location
  async deleteLocation(id, isActive) {
    return api.delete(`/locations/${id}`,{ data: { isActive } });
  },

  // Switch active location
  async switchLocation(locationId) {
    return api.post(`/locations/switch/${locationId}`);
  },

  // ============================================================
  // SIKKA PMS INTEGRATION
  // ============================================================

  // Configure Sikka for a location
  async configureSikka(locationId, data) {
    return api.post(`/pms/sikka/configure/${locationId}`, data);
  },

  // Test Sikka connection
  async testSikkaConnection(locationId) {
    return api.post(`/pms/sikka/test/${locationId}`);
  },

  // Get Sikka status for a location
  async getSikkaStatus(locationId) {
    return api.get(`/pms/sikka/status/${locationId}`);
  },

  // Refresh Sikka token
  async refreshSikkaToken(locationId) {
    return api.post(`/pms/sikka/refresh-token/${locationId}`);
  },

  // Sync patients from Sikka (mode: 'full' | 'incremental' | undefined for auto)
  async syncSikkaPatients(locationId, { mode } = {}) {
    return api.post(`/pms/sikka/sync-patients/${locationId}`, { mode });
  },

  // Sync appointments from Sikka (mode: 'full' | 'incremental' | undefined for auto)
  async syncSikkaAppointments(locationId, { mode, startDate, endDate } = {}) {
    return api.post(`/pms/sikka/sync-appointments/${locationId}`, { mode, startDate, endDate });
  },

  // Full sync from Sikka
  async syncSikkaAll(locationId) {
    return api.post(`/pms/sikka/sync-all/${locationId}`);
  },

  // Disconnect Sikka
  async disconnectSikka(locationId) {
    return api.post(`/pms/sikka/disconnect/${locationId}`);
  },

  // Get Sikka cron status
  async getSikkaCronStatus() {
    return api.get('/pms/sikka/cron/status');
  },

  // Trigger Sikka token refresh manually
  async triggerSikkaRefresh() {
    return api.post('/pms/sikka/cron/trigger');
  },

  async triggerPmsSync() {
    return api.post('/pms/sync/cron/trigger');
  },

  // ============================================================
  // NUMBER POOL (round-robin outbound numbers)
  // ============================================================
  async getNumberPool(locationId) {
    return api.get(`/number-pool/${locationId}`);
  },

  async searchNumberPool({ areaCode, country = "US", type = "local", limit = 20 } = {}) {
    const params = new URLSearchParams();
    if (areaCode) params.set("areaCode", areaCode);
    params.set("country", country);
    params.set("type", type);
    params.set("limit", String(limit));
    return api.get(`/number-pool/search?${params.toString()}`);
  },

  async purchasePoolNumber({ locationId, phoneNumber, friendlyName }) {
    return api.post("/number-pool/purchase", { locationId, phoneNumber, friendlyName });
  },

  async transferPoolNumber({ fromLocationId, toLocationId, phoneNumber }) {
    return api.post("/number-pool/transfer", { fromLocationId, toLocationId, phoneNumber });
  },

  async togglePool(locationId, enabled) {
    return api.patch(`/number-pool/${locationId}/toggle`, { enabled });
  },

  async togglePoolNumber(locationId, phoneNumber, isActive) {
    return api.patch(`/number-pool/${locationId}/numbers/${encodeURIComponent(phoneNumber)}/toggle`, { isActive });
  },

  async removePoolNumber(locationId, phoneNumber) {
    return api.delete(`/number-pool/${locationId}/numbers/${encodeURIComponent(phoneNumber)}`);
  },

  async updatePoolForwarding(locationId, forwardingNumber) {
    return api.patch(`/number-pool/${locationId}/forwarding`, { forwardingNumber });
  },
};

export default locationService;
